/* FullTextRenderer.js */

"use strict";
const uiCore = require("@gluenetworks/steve_ui_core");
const TextAreaControl = uiCore.Control.Textfield.Textarea;
const ButtonControl = uiCore.Control.Action.Button;
const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;

const WRAP_CLASS = "wrap-lines";

class FullTextRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = this._createRootElement();

        this._textArea = new TextAreaControl({darkTheme: true});

        this._wrapCheckbox = new CheckboxControl({
            label: "Wrap lines",
            darkTheme: true,
            labelShown: true,
        });

        this._copyButton = new ButtonControl({label: "Copy", darkTheme: true});
        this._closeButton = new ButtonControl({label: "Close", darkTheme: true, type: "additive"});

        this._registerListeners();
        this.render();
    }

    get rootElement() {
        return this.getRootElement();
    }

    get model() {
        return this.getModel();
    }

    /********************************************/
    /* START PUBLIC COMPONENT RENDERER METHODS  */
    /********************************************/

    render() {
        if (this._renderProm === null) {
            return Promise.resolve();
        }
        this._renderProm = this._renderProm || this._render();
        return this._renderProm;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    getRootElement() {
        return this._rootElem;
    }

    getModel() {
        return this._model;
    }

    /********************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS    */
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._closeButton.addListener(
            "UserClick",
            (this._onCloseClick = this._onCloseClick.bind(this))
        );
        this._copyButton.addListener(
            "UserClick",
            (this._onCopyClick = this._onCopyClick.bind(this))
        );
        this._wrapCheckbox.addListener(
            "UserValue",
            (this._onWrapChange = this._onWrapChange.bind(this))
        );
    }

    _unregisterListeners() {
        this._closeButton.removeListener("UserClick", this._onCloseClick);
        this._copyButton.removeListener("UserClick", this._onCopyClick);
        this._wrapCheckbox.removeListener("UserValue", this._onWrapChange);
    }

    _render() {
        this._rootElem.innerHTML = "";
        const root = document.createDocumentFragment();

        const title = document.createElement("h2");
        title.innerText = this._model.getTitle();
        root.appendChild(title);

        const textSection = document.createElement("section");
        textSection.className = "text-section";
        textSection.appendChild(this._textArea.renderer.rootElement);
        root.appendChild(textSection);

        root.appendChild(this._createActionsContainer());

        this._textArea.setValue(this._model.getText());
        this._textArea.setAttribute("readonly", true);
        this._wrapCheckbox.setValue(true);

        return new Promise((resolve /*,reject*/) => {
            this._rootElem.appendChild(root);
            resolve();
        });
    }

    _shutdown() {
        this._unregisterListeners();
        this._rootElem.remove();
        this._rootElem = null;

        return Promise.all([
            this._textArea.shutdown(),
            this._wrapCheckbox.shutdown(),
            this._copyButton.shutdown(),
            this._closeButton.shutdown(),
        ])
            .then(() => {
                this._model = null;
                this._shutdownProm = null;
                this._renderProm = null;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /********************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS   */
    /* START PRIVATE RENDERER METHODS           */
    /********************************************/

    _createRootElement() {
        const rootElement = document.createElement("full-text-veil-gl");
        rootElement.classList.add("full-text-veil", "modal_feedback_container", WRAP_CLASS);
        rootElement.component = this._model;
        return rootElement;
    }

    _createActionsContainer() {
        const container = document.createElement("section");
        container.className = "actions-container";
        container.appendChild(this._wrapCheckbox.renderer.rootElement);
        container.appendChild(this._copyButton.renderer.rootElement);
        container.appendChild(this._closeButton.renderer.rootElement);
        return container;
    }

    /********************************************/
    /* END PRIVATE RENDERER METHODS             */
    /* START HANDLER METHODS                    */
    /********************************************/

    _onCloseClick() {
        this._model.closePopUp();
    }

    _onWrapChange(newVal) {
        this._rootElem.classList.toggle(WRAP_CLASS, !!newVal);
    }

    _onCopyClick() {
        const text = this._model.getText();
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).catch(() => copyToClipboard(text));
            return;
        }
        copyToClipboard(text);
    }

    /********************************************/
    /* END HANDLER METHODS                      */
    /********************************************/
}

// A textarea rather than an input, so newlines in the tree survive the copy.
function copyToClipboard(value) {
    const fakeInput = document.createElement("textarea");

    fakeInput.value = value;
    fakeInput.readOnly = true;
    fakeInput.style.cssText = `
        position: fixed;
        bottom: 100%;
    `;
    document.body.appendChild(fakeInput);
    fakeInput.select();
    document.execCommand("copy");
    fakeInput.remove();
}

module.exports = FullTextRenderer;
